import React, { useMemo } from 'react'
import {
  TableRow,
  TableCell,
  Table,
  TableContainer,
  TableBody,
  Avatar,
  Skeleton,
} from '@mui/material'

import { Leader } from 'web-api-client'
import { useAppContext } from 'context/AppContext'
import { fNumber } from 'helpers/common'

interface LeadersTableProps {
  leaders: Leader[]
  isFetching: boolean
}

const LeadersTable: React.FC<LeadersTableProps> = ({ leaders, isFetching }) => {
  const { customer } = useAppContext()

  const leadersWithPlace = useMemo(
    () =>
      leaders.map((leader, index) => ({
        ...leader,
        place: index + 4,
      })),
    [leaders]
  )

  const customerIndex = useMemo(
    () =>
      leaders.findIndex(
        (leader) => leader.telegram_id === customer?.telegram_id
      ),
    [leaders, customer]
  )

  const hasRestLeadersCustomer = customerIndex !== -1

  const getLeadersContainingCustomer = () => {
    const start = Math.max(0, customerIndex - 3)
    const end = Math.min(leadersWithPlace.length, customerIndex + 4)

    if (end - start < 7) {
      const diff = 7 - (end - start)
      if (start - diff >= 0) {
        return leadersWithPlace.slice(start - diff, end)
      } else {
        return leadersWithPlace.slice(start, end + diff)
      }
    }

    return leadersWithPlace.slice(start, end)
  }

  const leaderArray = hasRestLeadersCustomer
    ? getLeadersContainingCustomer()
    : leadersWithPlace.slice(0, 7)

  return (
    <section className="LeaderBoard__users">
      <TableContainer sx={{ maxHeight: '300px' }}>
        <Table
          sx={{
            margin: '0 auto',
            borderCollapse: 'separate',
            borderSpacing: '0 4px',
            paddingY: '10px',
          }}
        >
          <TableBody>
            {isFetching
              ? Array.from(new Array(7)).map((_, index) => (
                  <TableRow
                    sx={{
                      '& .MuiTableCell-root': {
                        border: 'none',
                        backgroundColor: '#221C2B',
                        paddingY: '8px',
                        fontFamily: 'Montserrat Alternates',
                        height: '30px',
                      },
                    }}
                    key={index}
                  >
                    <TableCell>
                      <Skeleton variant="text" width="50%" />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width="70%" />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width="30%" />
                    </TableCell>
                  </TableRow>
                ))
              : leaderArray.map((leader) => (
                  <TableRow
                    key={leader.id}
                    sx={{
                      '& .MuiTableCell-root': {
                        border: 'none',
                        color:
                          leader.telegram_id === customer?.telegram_id
                            ? '#9CFF1E'
                            : '#fff',
                        backgroundColor: '#221C2B',
                        padding: '8px',
                        fontFamily: 'Montserrat Alternates',
                        height: '30px',
                      },
                    }}
                  >
                    <TableCell
                      sx={{
                        borderBottomLeftRadius: '15px',
                        borderTopLeftRadius: '15px',
                      }}
                    >
                      {leader.place}
                    </TableCell>

                    <TableCell sx={{ px: 0 }}>
                      {leader.photo_url ? (
                        <img
                          className="LeaderBoard__winner-photo"
                          width={28}
                          src={leader.photo_url}
                          alt=""
                          style={{
                            borderRadius: '50%',
                            verticalAlign: 'middle',
                          }}
                        />
                      ) : (
                        <Avatar
                          sx={{
                            width: '24px',
                            height: '24px',
                            padding: '2px',
                          }}
                        >
                          {leader.first_name.charAt(0)}
                          {leader.last_name.charAt(0)}
                        </Avatar>
                      )}
                    </TableCell>

                    <TableCell>
                      {leader.first_name} {leader.last_name}{' '}
                      {leader.telegram_id === customer?.telegram_id && '(you)'}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        borderBottomRightRadius: '15px',
                        borderTopRightRadius: '15px',
                      }}
                    >
                      {fNumber(leader.points)} pts
                    </TableCell>
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </TableContainer>
    </section>
  )
}

export default React.memo(LeadersTable)
